import { z } from 'zod'
import { RegistryIndexBlock } from './contractVersion.js'
import { DomeinSleutel, RondeSleutel } from './id.js'
import { Moment, Person } from './moment.js'
import { ProcessysteemId } from './processysteemRound.js'

/**
 * Domeinen en rondes (D-75, eisenboek 12.42, ontwerp 4.12, besluit van 1 november 2026).
 *
 * Een **domein** is een deel van NWO dat formulieren maakt en uitgeeft, zoals ENW of ZonMw, plus het
 * domein Algemeen voor de algemene formulieren van 2AT. Een domein is een rij in de opslag en geen
 * opsomming in de code: een nieuw domein vraagt geen codewijziging.
 *
 * Een **ronde** is in dit product alleen een sleutel die het processysteem ook gebruikt, met precies
 * één penvoerend domein en nul of meer meewerkende domeinen. De ronde als proces is van het
 * processysteem (eisenboek 12.40, D-35); voorlopig maakt Platformbeheer hem aan in de configurator, en
 * op termijn komt hij langs het koppelvlak van het processysteem en wordt hij op dezelfde sleutel
 * gekoppeld. Definities, de contracten van een ronde en publicaties hangen onder een ronde; het domein
 * volgt uit de ronde.
 *
 * **Strikt op de schrijfweg, ruim op de leesweg**, zoals bij de contracten (`contractVersion.ts`).
 */

/** Een domein zoals de opslagdienst het geeft. */
export const Domein = z.object({
  sleutel: DomeinSleutel,
  /** De afkorting waar een mens het domein aan herkent, zoals `ENW` of `NWA/KIC`. */
  afkorting: z.string().min(1).max(32),
  naam: z.string().min(1).max(200),
  /**
   * Alleen voor Platformbeheer (2AT), zoals Algemeen. Daar telt geen domeinrecht en wordt er geen
   * toegekend (D-76, migratie 0047); het gegeven staat hier, zodat het niet uit de sleutel hoeft te worden
   * afgeleid.
   */
  alleenPlatformbeheer: z.boolean(),
  aangemaaktOp: Moment,
  aangemaaktDoor: Person,
  /**
   * Opgeheven (migratie 0052): het domein staat niet meer in een keuzelijst en krijgt geen nieuwe ronde of
   * domeinrecht, maar blijft bestaan, met alles wat eronder hangt. Een domein wordt nooit verwijderd.
   * Optioneel, zodat een lezer van vóór 0052 het domein zoals altijd leest.
   */
  opgehevenOp: Moment.optional(),
  opgehevenDoor: Person.optional(),
})
export type Domein = z.infer<typeof Domein>

/** Het verzoek om een domein aan te maken. Een domein voor Platformbeheer maakt geen verzoek: dat is Algemeen, uit de migratie. */
export const DomeinRequest = z.strictObject({
  sleutel: DomeinSleutel,
  afkorting: Domein.shape.afkorting,
  naam: Domein.shape.naam,
  persoon: Person,
})
export type DomeinRequest = z.infer<typeof DomeinRequest>

/** Een ronde zoals de opslagdienst haar geeft. */
export const Ronde = z.object({
  sleutel: RondeSleutel,
  /** Het domein dat de ronde trekt; alleen daarvandaan komt het publicatieakkoord (D-44, D-76). */
  penvoerendDomein: DomeinSleutel,
  /** De domeinen die meewerken, op sleutel gesorteerd; het penvoerende domein staat er niet in. */
  meewerkendeDomeinen: z.array(DomeinSleutel),
  /**
   * Het id van de ronde bij het processysteem (D-75, besluit 3 tot en met 7 van het plan): alleen een ronde die
   * het processysteem vastlegde heeft er een. Een ronde met de hand en de rondes 2026-1 tot en met 2026-3
   * hebben hem niet. Optioneel, zodat een lezer van vóór dit werk de ronde zoals altijd leest.
   */
  rondeId: ProcessysteemId.optional(),
  /** Een doorlopende ronde (een ronde voor algemene formulieren) neemt later nog plekken aan. Ontbreekt bij een ronde met de hand. */
  doorlopend: z.boolean().optional(),
  aangemaaktOp: Moment,
  aangemaaktDoor: Person,
  /**
   * Opgeheven (migratie 0052): de ronde staat niet meer in een keuzelijst en neemt geen nieuw werk aan (een
   * nieuw formulier, een contractversie, een publicatie), maar alles wat eronder hangt blijft leesbaar en een
   * pod die draait, draait door. Een ronde wordt nooit verwijderd. Optioneel, zoals bij `Domein`.
   */
  opgehevenOp: Moment.optional(),
  opgehevenDoor: Person.optional(),
})
export type Ronde = z.infer<typeof Ronde>

/**
 * Het verzoek om een ronde aan te maken, met haar bouwblokken.
 *
 * De bouwblokken horen erbij en niet in een tweede aanroep: de bouwblokken van een ronde staan vast
 * voordat de formulieren worden gemaakt (D-41), en een ronde zonder registry-index heeft geen
 * contracten om tegen in te richten. De opslagdienst legt ze vast als de eerste registry-index van de
 * ronde; de contracten zelf komen daarna per blok als XSD (`POST /v1/contracten/:naam/versies?ronde=`).
 */
export const RondeRequest = z
  .strictObject({
    sleutel: RondeSleutel,
    penvoerendDomein: DomeinSleutel,
    meewerkendeDomeinen: z.array(DomeinSleutel).optional(),
    bouwblokken: z.array(RegistryIndexBlock).min(1),
    persoon: Person,
  })
  .superRefine((request, ctx) => {
    const seen = new Set<string>()
    ;(request.meewerkendeDomeinen ?? []).forEach((domein, i) => {
      if (domein === request.penvoerendDomein) {
        ctx.addIssue({ code: 'custom', path: ['meewerkendeDomeinen', i], message: `${domein} is het penvoerende domein en werkt dus niet mee` })
      }
      if (seen.has(domein)) ctx.addIssue({ code: 'custom', path: ['meewerkendeDomeinen', i], message: `${domein} staat er twee keer` })
      seen.add(domein)
    })
  })
export type RondeRequest = z.infer<typeof RondeRequest>

/**
 * Naam en afkorting van een domein wijzigen (migratie 0052). De sleutel verandert nooit: rechten, rondes en
 * de bibliotheek verwijzen ernaar. Minstens één van de twee.
 */
export const DomeinWijziging = z
  .strictObject({
    afkorting: Domein.shape.afkorting.optional(),
    naam: Domein.shape.naam.optional(),
    persoon: Person,
  })
  .refine((request) => request.afkorting !== undefined || request.naam !== undefined, { message: 'noem een nieuwe afkorting of naam' })
export type DomeinWijziging = z.infer<typeof DomeinWijziging>

/**
 * De domeinen van een ronde wijzigen (migratie 0052): het penvoerende domein en de meewerkende domeinen. De
 * sleutel verandert nooit. Het penvoerende domein verandert alleen zolang de ronde geen publicatie heeft: het
 * publicatieakkoord van een publicatie kwam uit dat domein (D-44, D-76), en wisselen zou achteraf verschuiven
 * wie over een bestaande publicatie gaat. Minstens één van de twee.
 */
export const RondeWijziging = z
  .strictObject({
    penvoerendDomein: DomeinSleutel.optional(),
    meewerkendeDomeinen: z.array(DomeinSleutel).optional(),
    persoon: Person,
  })
  .superRefine((request, ctx) => {
    if (request.penvoerendDomein === undefined && request.meewerkendeDomeinen === undefined) {
      ctx.addIssue({ code: 'custom', message: 'noem een penvoerend domein of de meewerkende domeinen' })
    }
    const seen = new Set<string>()
    ;(request.meewerkendeDomeinen ?? []).forEach((domein, i) => {
      if (domein === request.penvoerendDomein) {
        ctx.addIssue({ code: 'custom', path: ['meewerkendeDomeinen', i], message: `${domein} is het penvoerende domein en werkt dus niet mee` })
      }
      if (seen.has(domein)) ctx.addIssue({ code: 'custom', path: ['meewerkendeDomeinen', i], message: `${domein} staat er twee keer` })
      seen.add(domein)
    })
  })
export type RondeWijziging = z.infer<typeof RondeWijziging>

/** Een domein of ronde opheffen of weer in gebruik nemen; alleen wie het deed. */
export const OpheffenRequest = z.strictObject({ persoon: Person })
export type OpheffenRequest = z.infer<typeof OpheffenRequest>

/** Wat er aan een domein of ronde veranderde: de soort van een regel in het spoor (migratie 0052). */
export const DomeinRondeWijzigingSoort = z.enum(['naam', 'penvoerend', 'meewerkend', 'opgeheven', 'hersteld'])
export type DomeinRondeWijzigingSoort = z.infer<typeof DomeinRondeWijzigingSoort>

/**
 * Een regel in het spoor van domeinen en rondes (migratie 0052): wat er veranderde, de stand ervoor en
 * erna, wie en wanneer. Append-only: een regel wordt nooit gewijzigd of verwijderd, zoals bij de
 * domeinrechten (0047) en de bibliotheek (0048).
 */
export const DomeinRondeWijzigingEntry = z.strictObject({
  id: z.number().int().positive(),
  domein: DomeinSleutel.optional(),
  ronde: RondeSleutel.optional(),
  wat: DomeinRondeWijzigingSoort,
  oud: z.record(z.string(), z.unknown()),
  nieuw: z.record(z.string(), z.unknown()),
  door: Person,
  op: Moment,
})
export type DomeinRondeWijzigingEntry = z.infer<typeof DomeinRondeWijzigingEntry>

/**
 * Wie een standaardveld of sjabloon ziet (D-77): alleen het eigen domein, een lijst domeinen, of
 * alle domeinen. Wat er al was, staat op `alle`, want zo gedroeg de bibliotheek zich tot nu.
 */
export const Zichtbaarheid = z.enum(['eigen', 'lijst', 'alle'])
export type Zichtbaarheid = z.infer<typeof Zichtbaarheid>

/**
 * De zes rechten binnen de configurator (ontwerp 8.12, D-44). Ze staan als app-rol in Entra en zeggen
 * wat iemand mag; een domeinrecht zegt waar (D-76, besluit van 1 november 2026). Eén plek voor de namen:
 * de configurator leest ze uit het token, de opslagdienst toetst er een domeinrecht aan.
 */
export const ConfiguratorRight = z.enum(['Wijzigen', 'Publicatieakkoord', 'Regelbeheer', 'Concept-inzage', 'Contractbeheer', 'Bibliotheekbeheer'])
export type ConfiguratorRight = z.infer<typeof ConfiguratorRight>

/**
 * De app-rol van 2AT (D-76, ontwerp 8.12): alle domeinen, ook Algemeen, het aanmaken van domeinen en
 * rondes, en het toekennen van domeinrechten. Geen domeinrecht: wie hem heeft, heeft hem overal.
 */
export const PLATFORM_ROLE = 'Platformbeheer'

/**
 * Oude namen van een App Role en het recht dat ze nu geven (besluit van 1 november 2026).
 *
 * `Standaardveldbeheer` is vervangen door `Bibliotheekbeheer`, dat ook de sjablonen omvat. Wie de oude rol
 * in de tenant heeft, houdt tijdens de overgang zijn recht, in plaats van buitengesloten te worden tot
 * iemand hem de nieuwe rol geeft. **Weghalen** zodra elke toekenning van `Standaardveldbeheer` in de
 * App-registratie "eFormulieren configurator" is vervangen door `Bibliotheekbeheer`; dan ook de App Role
 * `Standaardveldbeheer` uitzetten en de oude wijzigroute van de store (`standaardveld-wijzigen`) laten
 * vervallen. Staat als open punt in `docs/dekking/na-m9-standaardvelden-versies-en-vier-ogen.md`.
 *
 * In het model en niet bij de server alleen, want sinds fase 5 leest ook het overzicht van de app-rollen in
 * de tab Platform hem (ontwerp 8.12): een oude rol telt daar mee zoals de server hem laat tellen.
 */
export const LEGACY_ROLES: Readonly<Record<string, ConfiguratorRight>> = {
  Standaardveldbeheer: 'Bibliotheekbeheer',
}

/**
 * Een domeinrecht: deze persoon mag dit recht uitoefenen in dit domein (D-76, migratie 0047), mits hij de
 * app-rol met dezelfde naam heeft. Een intrekking haalt de regel niet weg maar zet wie en wanneer; zo is
 * terug te lezen wie wanneer wat mocht. De persoon is de sleutel uit het token, `entra:<oid>`.
 */
export const Domeinrecht = z.object({
  id: z.number().int().positive(),
  persoon: Person,
  recht: ConfiguratorRight,
  domein: DomeinSleutel,
  toegekendOp: Moment,
  toegekendDoor: Person,
  ingetrokkenOp: Moment.optional(),
  ingetrokkenDoor: Person.optional(),
})
export type Domeinrecht = z.infer<typeof Domeinrecht>

/** Het verzoek om een domeinrecht toe te kennen; `door` is wie toekent (Platformbeheer). */
export const DomeinrechtRequest = z.strictObject({
  persoon: Person,
  recht: ConfiguratorRight,
  domein: DomeinSleutel,
  door: Person,
})
export type DomeinrechtRequest = z.infer<typeof DomeinrechtRequest>

/**
 * Het eigenaar-domein en de zichtbaarheid van een standaardveld of sjabloon (D-77, migratie 0046, fase 4).
 *
 * `zichtbaarVoor` is de lijst domeinen bij zichtbaarheid `lijst`, op sleutel gesorteerd, en anders leeg.
 * Het eigenaar-domein staat er nooit in: dat ziet zijn eigen item altijd. Een eigen vorm naast
 * `LibraryField` en `FormTemplate`, want die worden strikt gelezen, ook door een configurator van vóór
 * fase 4; een sleutel erbij zou die laten weigeren (3.5).
 */
export const LibraryDomain = z
  .strictObject({
    eigenaarDomein: DomeinSleutel,
    zichtbaarheid: Zichtbaarheid,
    zichtbaarVoor: z.array(DomeinSleutel),
  })
  .superRefine((domain, ctx) => libraryDomainRules(domain, ctx))
export type LibraryDomain = z.infer<typeof LibraryDomain>

/** De regels van `LibraryDomain`, ook voor de verzoekvormen: één plek voor wat een geldige lijst is. */
export function libraryDomainRules(
  domain: { readonly eigenaarDomein: string; readonly zichtbaarheid: Zichtbaarheid; readonly zichtbaarVoor: readonly string[] },
  ctx: z.RefinementCtx,
): void {
  if (domain.zichtbaarheid === 'lijst' && domain.zichtbaarVoor.length === 0) {
    ctx.addIssue({ code: 'custom', path: ['zichtbaarVoor'], message: 'bij zichtbaarheid lijst hoort minstens één domein' })
  }
  if (domain.zichtbaarheid !== 'lijst' && domain.zichtbaarVoor.length > 0) {
    ctx.addIssue({ code: 'custom', path: ['zichtbaarVoor'], message: `bij zichtbaarheid ${domain.zichtbaarheid} hoort geen lijst domeinen` })
  }
  const seen = new Set<string>()
  domain.zichtbaarVoor.forEach((domein, i) => {
    if (domein === domain.eigenaarDomein) {
      ctx.addIssue({ code: 'custom', path: ['zichtbaarVoor', i], message: `${domein} is het eigenaar-domein en ziet het item altijd` })
    }
    if (seen.has(domein)) ctx.addIssue({ code: 'custom', path: ['zichtbaarVoor', i], message: `${domein} staat er twee keer` })
    seen.add(domein)
  })
}

/** Een regel van `GET /v1/bibliotheek/domeinen`: het id van het item met zijn eigenaar-domein en zichtbaarheid. */
export const LibraryDomainEntry = z.strictObject({
  id: z.string().min(1),
  eigenaarDomein: DomeinSleutel,
  zichtbaarheid: Zichtbaarheid,
  zichtbaarVoor: z.array(DomeinSleutel),
})
export type LibraryDomainEntry = z.infer<typeof LibraryDomainEntry>

/** Het verzoek om eigenaar-domein en zichtbaarheid te zetten, met wie het deed; `zichtbaarVoor` alleen bij `lijst`. */
export const LibraryDomainRequest = z
  .strictObject({
    eigenaarDomein: DomeinSleutel,
    zichtbaarheid: Zichtbaarheid,
    zichtbaarVoor: z.array(DomeinSleutel).optional(),
    persoon: Person,
  })
  .superRefine((request, ctx) => libraryDomainRules({ ...request, zichtbaarVoor: request.zichtbaarVoor ?? [] }, ctx))
export type LibraryDomainRequest = z.infer<typeof LibraryDomainRequest>
